import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from "react";
import { FEATURED_PRESS_CTA_HREF, entryCard, FEATURED_PRESS_TITLE_SCALE, resolveTypeScale, } from "@diality/contracts";
import { Button } from "../components/ui/button";
import { cn } from "../lib/utils";
import { useBlockData } from "../render/data";
import { BlockLink, useBlockRender } from "../render/context";
/**
 * The frosted panel shared by the feature's copy half and each secondary card.
 * The prototype's GlassCard, reduced to the two things it actually contributes
 * here: a blue-tinted blur and the inset highlight/shade pair that gives the
 * panel its lit edge.
 */
const GLASS_PANEL = "bg-primary/15 backdrop-blur-[4px] backdrop-saturate-[1.2] shadow-[inset_1.5px_1.5px_0.5px_-1px_rgb(255_255_255/0.62),inset_-1.5px_-1.5px_0.5px_-1px_rgb(0_102_172/0.22)]";
/**
 * The title's size at each step the panel offers.
 *
 * LITERAL class strings, one per step: Tailwind's scanner only sees arbitrary
 * values written in source, so a ramp cannot be built from a number at runtime.
 *
 * Derived from THIS panel's own curve, not PersonGrid's — the two blocks ramp
 * differently, and copying the other's ratios would have changed how this one
 * behaves below 901px. Here min is .75 of the desktop size, the fluid term
 * .21875cqw, and the clamp's max IS the desktop size, so `m` reproduces the
 * string the panel shipped with, character for character.
 *
 * Because max equals desktop at every step, the `@min-[901px]:` override
 * currently changes nothing — it is kept because the panel already carried it
 * and because a step whose max and desktop later diverge needs it back.
 */
const TITLE_SIZE = {
    xl: "text-[clamp(72px,21cqw,96px)] @min-[901px]:text-[96px]",
    l: "text-[clamp(60px,17.5cqw,80px)] @min-[901px]:text-[80px]",
    m: "text-[clamp(48px,14cqw,64px)] @min-[901px]:text-[64px]",
    // Not offered by this panel's scale, so unreachable through the editor. Empty
    // rather than absent: the record stays total, and a step added to the scale
    // without a ramp here renders at the browser default instead of crashing.
    s: "",
    xs: "",
};
const META_EYEBROW = "type-eyebrow-lg";
const META_LINK = "type-eyebrow leading-[1.2] tracking-[0.08em]";
const CARD_TITLE = "font-sans text-3xl font-light leading-[1.2] text-brand-ink-surface text-pretty";
/** An absolute URL points off-site, so it opens in a new tab. */
const isExternal = (href) => /^https?:\/\//i.test(href);
function PressMeta({ item, inline = false, }) {
    return (_jsxs("p", { className: cn(META_EYEBROW, "m-0 text-foreground leading-[1.4]"), children: [_jsx("span", { className: inline ? undefined : "block", children: item.kicker ?? "Press Release" }), item.date && (_jsxs(_Fragment, { children: [inline && _jsx("span", { className: "mx-1", children: "\u00B7" }), _jsx("span", { className: `${inline ? undefined : "block"} font-medium`, children: item.date })] }))] }));
}
/**
 * The prototype's read-more glyph: a filled arrow leaving an open bracket.
 * Inlined rather than taken from lucide, whose ExternalLink is a stroked box
 * with a much smaller arrow, and so it can inherit the link colour.
 */
function ReadMoreIcon() {
    return (_jsxs("svg", { "aria-hidden": "true", className: "h-[14px] w-[15px] shrink-0", fill: "none", viewBox: "0 0 15 14", xmlns: "http://www.w3.org/2000/svg", children: [_jsx("path", { d: "M7.14233 0.700375C7.14236 0.313794 7.46254 -3.16231e-05 7.85703 2.39006e-09L14.2854 0.000483412C14.6798 0.000528313 14.9994 0.313972 14.9996 0.700375L14.9991 7.00085C14.9989 7.38724 14.6788 7.70029 14.2844 7.70025C13.8901 7.70005 13.5702 7.38682 13.5702 7.00036L13.5697 2.39065L7.29079 8.5437C7.01184 8.81702 6.55956 8.81704 6.28064 8.5437C6.00173 8.27034 6.00171 7.82713 6.28064 7.5538L12.5606 1.40075L7.85654 1.40027C7.46221 1.40015 7.14244 1.08679 7.14233 0.700375Z", fill: "currentColor" }), _jsx("path", { d: "M0 11.9001V2.10058C0 0.940835 0.95939 0.000678411 2.14286 0.000678411H4.64286V1.40061H2.14286C1.74837 1.40061 1.42857 1.714 1.42857 2.10058V11.9001C1.42857 12.2867 1.74837 12.6001 2.14286 12.6001H12.8571C13.2516 12.6001 13.5714 12.2867 13.5714 11.9001V10.1502H15V11.9001C15 13.0598 14.0406 14 12.8571 14H2.14286C0.95939 14 0 13.0598 0 11.9001Z", fill: "currentColor" })] }));
}
function ReadMoreLink({ item, label, }) {
    return (_jsxs(BlockLink, { className: cn(META_LINK, "inline-flex shrink-0 items-center gap-3 leading-none text-primary", "transition-colors duration-200 ease-in-out hover:text-brand-blue-dark focus-visible:text-brand-blue-dark [&>svg]:transition-transform [&>svg]:duration-200 [&>svg]:ease-in-out hover:[&>svg]:translate-x-[4px] focus-visible:[&>svg]:translate-x-[4px] motion-reduce:transition-none motion-reduce:[&>svg]:transition-none"), href: item.href, target: isExternal(item.href) ? "_blank" : undefined, children: [_jsx("span", { "aria-hidden": "true", children: label }), _jsx("span", { className: "sr-only", children: `${label}: ${item.title}` }), _jsx(ReadMoreIcon, {})] }));
}
/**
 * FeaturedPress — the Featured Press Releases panel from Our Story.
 *
 * One release leads with an image sitting flush on a frosted copy panel, with
 * the remainder two-up beneath it. Header copy is authored; the releases are
 * the site's press-release summaries.
 */
export function FeaturedPress({ heading, headingSize = "m", body, ctaLabel, ctaHref, feature, items = [], readMoreLabel = "Read more", className, }) {
    const headingId = React.useId();
    const hasHeader = Boolean(heading || body || (ctaHref && ctaLabel));
    return (_jsx("section", { "aria-labelledby": heading ? headingId : undefined, 
        // Its own query container, so the breakpoints below resolve whether or
        // not a BlockRenderer wraps this block. Layout and gutters sit on the
        // child, because an element cannot answer its own container query.
        className: cn("@container w-full", className), children: _jsxs("div", { className: "flex w-full flex-col items-center gap-12 px-[6.667cqw] @min-[901px]:px-12 @min-[1201px]:px-[154px]", children: [hasHeader ? (_jsxs("header", { className: "flex w-[clamp(200px,100%,1066px)] flex-col items-center gap-4 text-center", children: [(heading || body) && (_jsxs("div", { className: "flex flex-col items-center gap-2", children: [heading ? (_jsx("h2", { className: cn("font-display font-light leading-[1.04] tracking-[-0.02em] text-brand-blue", TITLE_SIZE[headingSize]), "data-featured-press-title": true, "data-heading-size": headingSize, id: headingId, children: heading })) : null, body ? (_jsx("p", { className: "w-full text-lg font-light leading-[1.55] text-foreground @min-[901px]:w-[696px] @min-[901px]:text-xl", "data-featured-press-description": true, children: body })) : null] })), ctaHref && ctaLabel ? (_jsx("div", { "data-featured-press-cta": true, children: _jsx(Button, { asChild: true, children: _jsx(BlockLink, { href: ctaHref, children: ctaLabel }) }) })) : null] })) : null, _jsxs("div", { className: "flex w-full max-w-[1132px] flex-col items-center gap-6", children: [_jsxs("article", { className: "flex w-full flex-col rounded-lg @min-[1201px]:max-w-[747px]", children: [feature.image ? (_jsx("img", { alt: feature.imageAlt ?? "", className: "relative z-10 block aspect-video w-full rounded-t-lg object-cover @min-[901px]:aspect-[747/420]", src: feature.image })) : null, _jsxs("div", { className: cn("z-0 flex w-full flex-col items-start gap-[22px] px-6 py-8 @min-[901px]:p-10", feature.image ? "rounded-b-lg" : "rounded-lg", GLASS_PANEL), children: [_jsxs("div", { className: "flex w-full flex-col gap-2", children: [_jsx(PressMeta, { item: feature, inline: true }), _jsx("h3", { className: CARD_TITLE, children: feature.title })] }), _jsx(ReadMoreLink, { item: feature, label: readMoreLabel })] })] }), items.length > 0 && (_jsx("div", { className: "grid w-full grid-cols-1 gap-6 @min-[901px]:grid-cols-2 @min-[1201px]:max-w-[747px]", children: items.map((item) => (_jsxs("article", { className: cn("flex flex-col items-start gap-2 rounded-lg px-6 py-8 @min-[901px]:p-10", GLASS_PANEL), children: [_jsx(PressMeta, { item: item }), _jsxs("div", { className: "flex flex-col items-start gap-[22px]", children: [_jsx("h3", { className: CARD_TITLE, children: item.title }), _jsx(ReadMoreLink, { item: item, label: readMoreLabel })] })] }, item.id))) }))] })] }) }));
}
/** Sample data for component stories; never used by the CMS-backed block. */
export const FEATURED_PRESS_PLACEHOLDER_FEATURE = {
    id: "first-patient-treated-moda-flx-home-ide",
    title: "Diality Announces First Patient Treated in Moda-flx Home IDE PRESCRIBE Diality Clinical Trial",
    href: "/news/first-patient-treated-moda-flx-home-ide",
    date: "September 11, 2025",
    image: "https://picsum.photos/seed/diality-press-featured/1494/840",
    imageAlt: "Diality team members collaborating",
};
export const FEATURED_PRESS_PLACEHOLDER_ITEMS = [
    {
        id: "peter-donato-cfo",
        title: "Diality Appoints Peter Donato as Chief Financial Officer",
        href: "/news/peter-donato-cfo",
        date: "February 24, 2026",
    },
    {
        id: "chris-springer-board",
        title: "Diality Names Dr. Chris Springer to Board of Directors",
        href: "/news/chris-springer-board",
        date: "March 25, 2025",
    },
];
/** Curated published entries, loaded by the route loader or CMS data provider. */
function featuredPressRequest(data) {
    return data.collectionId
        ? { kind: "collection-entries", collectionId: data.collectionId }
        : null;
}
export function FeaturedPressBlock({ data }) {
    const { entries, collection, status } = useBlockData(featuredPressRequest(data));
    const { resolveImage, placeholders } = useBlockRender();
    const byId = new Map(entries.map((entry) => [entry.id, entry]));
    const ids = [
        ...new Set([data.leadArticleId ?? "", ...(data.articleIds ?? [])].filter(Boolean)),
    ];
    const cards = ids.flatMap((id) => {
        const entry = byId.get(id);
        return entry
            ? [entryCard({ display: collection?.display ?? {} }, entry, resolveImage)]
            : [];
    });
    const lead = cards.find((card) => card.id === data.leadArticleId);
    const additional = cards.filter((card) => card.id !== data.leadArticleId);
    const hasPair = data.articleIds?.length === 2 && additional.length === 2;
    if (status !== "ready" || !lead?.image || !hasPair) {
        return placeholders === "editor" ? (_jsx("p", { className: "p-8 text-muted-foreground", role: "status", children: status === "loading"
                ? "Loading featured articles…"
                : status === "error"
                    ? "Featured articles could not be loaded."
                    : "Choose a published lead article with a picture and exactly two different additional articles from the collection." })) : null;
    }
    return (_jsx(FeaturedPress, { heading: data.title, headingSize: resolveTypeScale(FEATURED_PRESS_TITLE_SCALE, data.titleSize), body: data.description, ctaLabel: data.ctaLabel, ctaHref: collection?.basePath ?? FEATURED_PRESS_CTA_HREF, feature: {
            ...lead,
            kicker: lead.category,
            image: lead.image.url,
            imageAlt: lead.image.alt,
        }, items: additional.map((card) => ({ ...card, kicker: card.category })) }));
}
export function featuredPressFromBlock(block, _resolve) {
    const scalars = block.scalars ?? {};
    return {
        collectionId: scalars.collectionId ?? "",
        leadArticleId: scalars.leadArticleId ?? "",
        articleIds: (block.repeats?.articles ?? [])
            .map((row) => row.scalars?.articleId ?? "")
            .filter(Boolean),
        title: scalars.title ?? "",
        // A panel stored before the size control existed has no such scalar, so it
        // resolves to the block's default — which is deliberately one step LARGER
        // than the 64px it used to draw. See FEATURED_PRESS_TITLE_SCALE.
        titleSize: resolveTypeScale(FEATURED_PRESS_TITLE_SCALE, scalars.titleSize),
        description: scalars.description ?? "",
        ctaLabel: scalars.ctaLabel ?? "",
    };
}
export const featuredPressBlockEntry = {
    Component: FeaturedPressBlock,
    fromBlock: featuredPressFromBlock,
    dataRequests: (block) => {
        const request = featuredPressRequest({
            collectionId: block.scalars?.collectionId,
            title: "",
            description: "",
            ctaLabel: "",
        });
        return request ? [request] : [];
    },
};
//# sourceMappingURL=featured-press.js.map